import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Pin, Edit2, ChevronRight, ShoppingBag, Sparkles, Eye, X, List, Flame, Sun, Moon, Clock, CheckCircle2 } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import ChecklistView from '@/components/ChecklistView';

export default function MyChecklistsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('mine');
  const [checklists, setChecklists] = useState([]);
  const [pinnedChecklistId, setPinnedChecklistId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checklistMax, setChecklistMax] = useState(5);
  const [brandChecklists, setBrandChecklists] = useState([]);
  const [expandedBrandChecklist, setExpandedBrandChecklist] = useState(null);
  const [routinesByChecklist, setRoutinesByChecklist] = useState({}); // { checklist_id: routine }
  const [routineModalFor, setRoutineModalFor] = useState(null); // checklist_id being scheduled
  const [creatingRoutine, setCreatingRoutine] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchChecklists();
    fetchLimits();
    fetchBrandChecklists();
    fetchRoutines();
  }, []);

  const fetchRoutines = async () => {
    try {
      const res = await authFetch(`${API}/routines`);
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(r => { map[r.checklist_id] = r; });
        setRoutinesByChecklist(map);
      }
    } catch (e) { /* silent */ }
  };

  const handleCreateRoutine = async (checklistId, schedule) => {
    setCreatingRoutine(true);
    try {
      const res = await authFetch(`${API}/routines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist_id: checklistId, schedule })
      });
      if (res.ok) {
        const routine = await res.json();
        setRoutinesByChecklist(prev => ({ ...prev, [checklistId]: routine }));
        setRoutineModalFor(null);
        toast.success('Routine created! Check in daily to build your streak.');
        navigate('/routines');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create routine');
      }
    } catch (e) {
      console.error('Error creating routine:', e);
      toast.error('Failed to create routine');
    } finally {
      setCreatingRoutine(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setPinnedChecklistId(userData.pinned_checklist_id);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await authFetch(`${API}/checklists/limits`);
      if (response.ok) {
        const data = await response.json();
        setChecklistMax(data.max);
      }
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await authFetch(`${API}/checklists`);
      if (response.ok) {
        const data = await response.json();
        setChecklists(data);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandChecklists = async () => {
    try {
      const response = await fetch(`${API}/brand-checklists`);
      if (response.ok) {
        const data = await response.json();
        setBrandChecklists(data);
      }
    } catch (error) {
      console.error('Error fetching brand checklists:', error);
    }
  };

  const handleShareInChat = (checklist) => {
    navigate('/messages', { state: { shareChecklist: checklist } });
    toast.success('Select a conversation to share your checklist');
  };

  const handleDelete = async (checklistId) => {
    if (!window.confirm('Are you sure you want to delete this checklist?')) return;
    try {
      const response = await authFetch(`${API}/checklists/${checklistId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Checklist deleted');
        setChecklists(checklists.filter(c => c.checklist_id !== checklistId));
        if (checklistId === pinnedChecklistId) setPinnedChecklistId(null);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete checklist');
      }
    } catch (error) {
      console.error('Error deleting checklist:', error);
      toast.error('Failed to delete checklist');
    }
  };

  const handlePin = async (checklistId) => {
    try {
      const response = await authFetch(`${API}/checklists/${checklistId}/pin`, { method: 'POST' });
      if (response.ok) {
        toast.success('Checklist pinned! It will auto-attach to all new posts');
        setPinnedChecklistId(checklistId);
      } else {
        throw new Error('Failed to pin');
      }
    } catch (error) {
      console.error('Error pinning checklist:', error);
      toast.error('Failed to pin checklist');
    }
  };

  const handleUnpin = async () => {
    try {
      const response = await authFetch(`${API}/checklists/unpin`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Checklist unpinned');
        setPinnedChecklistId(null);
      } else {
        throw new Error('Failed to unpin');
      }
    } catch (error) {
      console.error('Error unpinning checklist:', error);
      toast.error('Failed to unpin checklist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading checklists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Checklists</h1>
          <button
            data-testid="create-checklist-btn"
            onClick={() => navigate('/create-checklist')}
            className="p-2 hover:bg-primary/10 rounded-full transition-colors"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-4 flex gap-1">
          <button
            data-testid="tab-my-checklists"
            onClick={() => setActiveTab('mine')}
            className={`flex-1 py-2.5 text-sm font-semibold text-center transition-colors relative ${
              activeTab === 'mine' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="flex items-center justify-center gap-1.5">
              <List className="w-4 h-4" />
              My Checklists
            </span>
            {activeTab === 'mine' && (
              <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            data-testid="tab-brand-routines"
            onClick={() => setActiveTab('brands')}
            className={`flex-1 py-2.5 text-sm font-semibold text-center transition-colors relative ${
              activeTab === 'brands' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="flex items-center justify-center gap-1.5">
              <Sparkles className="w-4 h-4" />
              Brand Routines
            </span>
            {activeTab === 'brands' && (
              <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {activeTab === 'mine' ? (
          <>
            {checklists.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg mb-4">No checklists yet!</p>
                <button
                  data-testid="create-first-checklist-btn"
                  onClick={() => navigate('/create-checklist')}
                  className="btn-primary"
                >
                  Create Your First Checklist
                </button>
                {brandChecklists.length > 0 && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    Need inspiration? Check out the{' '}
                    <button onClick={() => setActiveTab('brands')} className="text-primary font-semibold underline underline-offset-2" data-testid="switch-to-brands-link">
                      Brand Routines
                    </button>{' '}
                    tab for examples!
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  {checklists.length} / {checklistMax} checklists created
                </p>
                {checklists.map(checklist => (
                  <div key={checklist.checklist_id} className="relative bg-white rounded-xl border border-border">
                    <div className="flex items-center justify-between p-4 pb-2">
                      <div className="flex-1 min-w-0" />
                      <div className="flex gap-2 shrink-0">
                        {checklist.checklist_id === pinnedChecklistId ? (
                          <button
                            data-testid={`unpin-checklist-${checklist.checklist_id}`}
                            onClick={() => handleUnpin()}
                            className="p-2 bg-primary text-white border border-primary rounded-full hover:bg-primary/90 transition-colors shadow-sm"
                            title="Unpin checklist"
                          >
                            <Pin className="w-4 h-4 fill-current" />
                          </button>
                        ) : (
                          <button
                            data-testid={`pin-checklist-${checklist.checklist_id}`}
                            onClick={() => handlePin(checklist.checklist_id)}
                            className="p-2 bg-white border border-border rounded-full hover:bg-primary/10 hover:border-primary transition-colors shadow-sm"
                            title="Pin checklist (auto-attach to all posts)"
                          >
                            <Pin className="w-4 h-4" />
                          </button>
                        )}
                        {routinesByChecklist[checklist.checklist_id] ? (
                          <button
                            data-testid={`checkin-routine-${checklist.checklist_id}`}
                            onClick={() => navigate('/routines')}
                            className="p-2 bg-gradient-to-br from-orange-400 to-rose-400 text-white border border-orange-300 rounded-full hover:opacity-90 transition-opacity shadow-sm"
                            title="Open routine to check in"
                          >
                            <Flame className="w-4 h-4 fill-current" />
                          </button>
                        ) : (
                          <button
                            data-testid={`make-routine-${checklist.checklist_id}`}
                            onClick={() => setRoutineModalFor(checklist.checklist_id)}
                            className="p-2 bg-white border border-border rounded-full hover:bg-orange-50 hover:border-orange-400 hover:text-orange-500 transition-colors shadow-sm"
                            title="Make this checklist a daily routine"
                          >
                            <Flame className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`edit-checklist-${checklist.checklist_id}`}
                          onClick={() => navigate(`/edit-checklist/${checklist.checklist_id}`)}
                          className="p-2 bg-white border border-border rounded-full hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-colors shadow-sm"
                          title="Edit checklist"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-checklist-${checklist.checklist_id}`}
                          onClick={() => handleDelete(checklist.checklist_id)}
                          className="p-2 bg-white border border-border rounded-full hover:bg-destructive hover:text-white hover:border-destructive transition-colors shadow-sm"
                          title="Delete checklist"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {checklist.checklist_id === pinnedChecklistId && (
                      <div className="px-4 pb-2">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          <Pin className="w-3 h-3 fill-current" />
                          Pinned - Auto-attaches to all posts
                        </span>
                      </div>
                    )}
                    <div className="px-4 pb-4">
                      <ChecklistView checklist={checklist} showShare={true} onShareInChat={handleShareInChat} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Brand Routines Tab */
          <div className="space-y-4">
            {brandChecklists.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No brand routines available yet.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Expert-curated routines from top brands. Tap to explore products & shop directly.</p>
                {brandChecklists.map(bc => (
                  <button
                    key={bc.brand_checklist_id}
                    data-testid={`brand-routine-${bc.brand_checklist_id}`}
                    onClick={() => setExpandedBrandChecklist(bc)}
                    className="w-full bg-white rounded-2xl border border-border p-4 text-left shadow-sm hover:shadow-md hover:border-primary/30 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold text-foreground leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{bc.brand}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{bc.name}</p>
                        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary/10 text-primary mt-1.5">{bc.skin_type}</span>
                      </div>
                      <div className="flex items-center gap-1 text-primary text-xs font-medium shrink-0 mt-1">
                        <Eye className="w-3.5 h-3.5" />
                        <span>{bc.product_details?.length || 0}</span>
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                    {bc.description && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">{bc.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(bc.product_details || []).slice(0, 4).map(p => (
                        <span key={p.product_id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-100 rounded-full text-[10px] text-muted-foreground">
                          {p.product_type?.replace('_', ' ')}
                        </span>
                      ))}
                      {(bc.product_details?.length || 0) > 4 && (
                        <span className="inline-flex items-center px-2 py-1 bg-primary/5 border border-primary/10 rounded-full text-[10px] text-primary font-medium">
                          +{bc.product_details.length - 4} more
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Brand Checklist Detail Modal */}
      {expandedBrandChecklist && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setExpandedBrandChecklist(null)}>
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[80vh] overflow-y-auto mb-16 sm:mb-0"
            onClick={e => e.stopPropagation()}
            data-testid="brand-checklist-modal"
          >
            <div className="sticky top-0 bg-white z-10 px-5 pt-5 pb-3 border-b border-border rounded-t-3xl">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{expandedBrandChecklist.brand}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">{expandedBrandChecklist.name}</p>
                  <span className="inline-block px-2.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary/10 text-primary mt-1.5">{expandedBrandChecklist.skin_type}</span>
                </div>
                <button onClick={() => setExpandedBrandChecklist(null)} className="p-1.5 rounded-full hover:bg-black/5 shrink-0" data-testid="close-brand-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-5 py-4">
              {expandedBrandChecklist.description && (
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{expandedBrandChecklist.description}</p>
              )}

              <div className="space-y-3">
                {(expandedBrandChecklist.product_details || []).map((product, idx) => (
                  <div key={product.product_id} className="p-3 rounded-xl bg-gray-50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-primary">{idx + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground leading-tight">{product.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{product.product_type?.replace('_', ' ')} {product.price ? `· ${product.price}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2.5 ml-11">
                      {product.purchase_url && (
                        <a
                          href={product.purchase_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`buy-${product.product_id}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-white text-xs font-semibold hover:bg-primary/90 transition-colors"
                          onClick={e => e.stopPropagation()}
                        >
                          <ShoppingBag className="w-3 h-3" />
                          Buy
                        </a>
                      )}
                      <button
                        data-testid={`add-to-checklist-${product.product_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedBrandChecklist(null);
                          navigate('/create-checklist', { state: { preselectedProduct: product.product_id } });
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-primary text-primary text-xs font-semibold hover:bg-primary/5 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Add to Checklist
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-border space-y-3">
                <button
                  data-testid="create-similar-checklist"
                  onClick={() => { setExpandedBrandChecklist(null); navigate('/create-checklist'); }}
                  className="w-full btn-primary py-3 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Your Own Checklist Like This
                </button>
                <button
                  data-testid="view-brand-profile"
                  onClick={() => { setExpandedBrandChecklist(null); navigate(`/brand/${encodeURIComponent(expandedBrandChecklist.brand)}`); }}
                  className="w-full py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors flex items-center justify-center gap-1"
                >
                  View all {expandedBrandChecklist.brand} products
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Make Routine Modal */}
      {routineModalFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="make-routine-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !creatingRoutine && setRoutineModalFor(null)} />
          <div className="relative bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl">
            <button
              onClick={() => !creatingRoutine && setRoutineModalFor(null)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              disabled={creatingRoutine}
              data-testid="close-routine-modal"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-400 to-rose-400 rounded-full flex items-center justify-center">
                <Flame className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Make this checklist a routine
              </h3>
              <p className="text-sm text-muted-foreground">
                Pick when you follow it. Check in daily to build streaks and earn badges.
              </p>
              <div className="grid grid-cols-3 gap-2 pt-2">
                <button
                  data-testid="routine-morning"
                  disabled={creatingRoutine}
                  onClick={() => handleCreateRoutine(routineModalFor, 'morning')}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-amber-400 hover:bg-amber-50 transition-all disabled:opacity-50"
                >
                  <Sun className="w-6 h-6 text-amber-500" />
                  <span className="text-xs font-bold">Morning</span>
                </button>
                <button
                  data-testid="routine-night"
                  disabled={creatingRoutine}
                  onClick={() => handleCreateRoutine(routineModalFor, 'night')}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-indigo-400 hover:bg-indigo-50 transition-all disabled:opacity-50"
                >
                  <Moon className="w-6 h-6 text-indigo-500" />
                  <span className="text-xs font-bold">Night</span>
                </button>
                <button
                  data-testid="routine-everyday"
                  disabled={creatingRoutine}
                  onClick={() => handleCreateRoutine(routineModalFor, 'both')}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50"
                >
                  <Clock className="w-6 h-6 text-primary" />
                  <span className="text-xs font-bold">Everyday</span>
                </button>
              </div>
              {creatingRoutine && (
                <p className="text-xs text-muted-foreground">Creating routine…</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
